import { useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../i18n/LanguageContext";

const MAX_PHOTOS = 6;

function fileToDataUrl(file, maxDim = 720, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let w = img.width,
          h = img.height;
        if (w > h && w > maxDim) {
          h = Math.round(h * (maxDim / w));
          w = maxDim;
        } else if (h >= w && h > maxDim) {
          w = Math.round(w * (maxDim / h));
          h = maxDim;
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Captura una o varias fotos de evidencia. `value` es siempre un arreglo
 * (aunque solo tenga una foto) para mantener un solo componente para
 * "foto del paquete" y "fotos de evidencia de entrega".
 */
export default function MultiPhotoInput({ value, onChange, label, max = MAX_PHOTOS }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = value || [];

  const handle = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, max - photos.length);
    if (!files.length) return;
    setBusy(true);
    try {
      const newOnes = await Promise.all(files.map((f) => fileToDataUrl(f)));
      onChange([...photos, ...newOnes]);
    } finally {
      setBusy(false);
    }
    e.target.value = "";
  };

  const removeAt = (i) => onChange(photos.filter((_, idx) => idx !== i));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label || `${t("common.photos")} (${t("common.optional")})`}
      </Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: photos.length ? 1 : 0 }}>
        {photos.map((src, i) => (
          <Box key={i} sx={{ position: "relative" }}>
            <Box component="img" src={src} sx={{ width: 70, height: 70, objectFit: "cover", borderRadius: 1 }} />
            <IconButton
              size="small"
              onClick={() => removeAt(i)}
              sx={{ position: "absolute", top: -8, right: -8, bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ))}
      </Stack>
      {photos.length < max && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhotoCameraIcon fontSize="small" />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? t("common.processing") : t("common.takeOrUploadPhoto")}
        </Button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={handle} />
    </Box>
  );
}
